import { useHandler } from "@redotech/react-util/hook";
import {
  applyDynamicVariableSyntaxLiquid,
  DynamicVariable,
  SchemaType,
} from "@redotech/redo-flows-common/schemas/schemas";
import { SchemaField } from "@redotech/redo-flows-core-schema-common/type-system/schema";
import BracketEllipsesIcon from "@redotech/redo-icons/brackets-ellipses_filled.svg";
import FaceSmileIcon from "@redotech/redo-icons/face-smile.svg";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { DynamicVariableSelectionModal } from "@redotech/redo-web/dynamic-variable-selection-modal";
import {
  BaseEmoji,
  CustomEmoji,
  EmojiSelectionModal,
} from "@redotech/redo-web/emoji-selection-modal";
import { InlineVariableMenu } from "@redotech/redo-web/inline-variable-menu";
import { useInlineVariableAutocomplete } from "@redotech/redo-web/use-inline-variable-autocomplete";
import { memo, RefObject, useEffect, useRef, useState } from "react";

export const EmailTextInputToolbar = memo(
  function EmailBuilderTextInputToolbar({
    text,
    setText,
    inputRef,
    schemaType,
    availableDynamicVariables,
  }: {
    text: string;
    setText: (value: string) => void;
    inputRef: RefObject<HTMLInputElement | HTMLTextAreaElement | null>;
    schemaType: SchemaType | null;
    availableDynamicVariables: readonly SchemaField[];
  }) {
    const [emojiPickerOpen, setEmojiPickerOpen] = useState(false);
    const [variablePickerOpen, setVariablePickerOpen] = useState(false);
    const emojiButtonRef = useRef<HTMLButtonElement>(null);
    const variableButtonRef = useRef<HTMLButtonElement>(null);

    const handleEmojiPickerOpen = useHandler(() => {
      setEmojiPickerOpen(true);
    });

    const handleEmojiPicked = useHandler((emoji: BaseEmoji | CustomEmoji) => {
      if (!("native" in emoji)) {
        return; // Rule out CustomEmoji
      }

      const insertPosition = inputRef.current?.selectionStart ?? text.length;
      setText(
        text.slice(0, insertPosition) +
          emoji.native +
          text.slice(insertPosition),
      );
    });

    const handleVariablePicked = useHandler((variable: DynamicVariable) => {
      const variableText = applyDynamicVariableSyntaxLiquid({
        ...variable,
        isHtml: false,
        schemaType,
      });

      const insertPosition = inputRef.current?.selectionStart ?? text.length;
      setText(
        text.slice(0, insertPosition) +
          variableText +
          text.slice(insertPosition),
      );

      setVariablePickerOpen(false);
    });

    const handleVariablePickerClose = useHandler(() => {
      setVariablePickerOpen(false);
    });

    const [input, setInput] = useState<HTMLElement | null>(null);
    const caretToRestore = useRef<number | null>(null);
    useEffect(() => setInput(inputRef.current), [inputRef]);

    const inline = useInlineVariableAutocomplete({
      enabled: !!schemaType,
      target: input,
      variables: availableDynamicVariables,
      textBeforeCaret: () => {
        const element = inputRef.current;
        return element
          ? element.value.slice(
              0,
              element.selectionStart ?? element.value.length,
            )
          : null;
      },
      replaceQuery: (startIndex, field) => {
        const element = inputRef.current;
        const caret = element?.selectionStart ?? text.length;
        const token = applyDynamicVariableSyntaxLiquid({
          key: field.key,
          isHtml: false,
          schemaType,
        });
        setText(text.slice(0, startIndex) + token + text.slice(caret));
        caretToRestore.current = startIndex + token.length;
      },
    });

    // The caret can only be placed once the replacement text has been applied.
    useEffect(() => {
      const cursor = caretToRestore.current;
      const element = inputRef.current;
      if (cursor == null || !element) {
        return;
      }
      caretToRestore.current = null;
      element.focus();
      element.setSelectionRange(cursor, cursor);
    }, [text, inputRef]);

    return (
      <>
        <RedoButton
          aria-label="Insert emoji"
          hierarchy="tertiary"
          IconLeading={FaceSmileIcon}
          onClick={handleEmojiPickerOpen}
          ref={emojiButtonRef}
          size="sm"
        />
        {schemaType && (
          <RedoButton
            aria-label="Insert variable"
            hierarchy="tertiary"
            IconLeading={BracketEllipsesIcon}
            onClick={() => setVariablePickerOpen(!variablePickerOpen)}
            ref={variableButtonRef}
            size="sm"
          />
        )}
        <EmojiSelectionModal
          anchor={emojiButtonRef.current}
          handleEmojiSelected={handleEmojiPicked}
          onClose={() => setEmojiPickerOpen(false)}
          open={emojiPickerOpen}
          placement="left"
          theme="light"
        />
        {inline.open && (
          <InlineVariableMenu
            activeIndex={inline.activeIndex}
            anchor={input}
            fields={inline.fields}
            onActiveIndexChange={inline.setActiveIndex}
            onClickAway={inline.close}
            onSelect={inline.select}
          />
        )}
        {variablePickerOpen && (
          <DynamicVariableSelectionModal
            dynamicVariables={availableDynamicVariables}
            handleDynamicVariableSelected={handleVariablePicked}
            onClose={handleVariablePickerClose}
            open={variablePickerOpen}
          />
        )}
      </>
    );
  },
);
